/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import AppConst from '../../../constants/app.constant';
import ChartConst from '../../../constants/chart.constant';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import d3 from '../../../globalexports/d3.export';

function tempChartService() {
  const content = {};
  content.drawChart = (
    sourceData,
    settings,
    chartData,
    chartObj,
    chartHeight,
    width,
    topSpace,
    x,
    y1,
    y2,
    tempMinMaxPlottingData,
    yAxisRectWidth,
    yAxisRightRectWidth,
  ) => {
    if (!sourceData) {
      return null;
    }
    let temperatureConfig;
    const data = sourceData.forecast;
    if (settings.temperatureUnit === AppConst.temperatureUnits.fahrenheit.key) {
      // If preference is fahrenheit
      temperatureConfig = AppConst.temperatureUnits.fahrenheit;
      temperatureConfig.gradientConfig = ChartConst.temperature.fahrenheit.gradientConfig;
      temperatureConfig.gradientConfigArea = ChartConst.temperature.fahrenheit.gradientConfigArea;
    } else {
      // If preference is celsius
      temperatureConfig = AppConst.temperatureUnits.celsius;
      temperatureConfig.gradientConfig = ChartConst.temperature.celsius.gradientConfig;
      temperatureConfig.gradientConfigArea = ChartConst.temperature.celsius.gradientConfigArea;
    }

    // Y-Axis left side ChartConst
    const yAxisLeft = d3.axisRight()
      .scale(y1)
      .ticks(ChartConst.GraphConfig.yAxis.ticks3)
      .tickFormat((d) => d + ChartConst.ChartLabelSpecs.tmp.tooltip.unit)
      .tickSizeInner(-width)
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);
    // yAxisLeftMinor is for drawing ticks line inside chart
    const yAxisLeftMinor = d3.axisLeft()
      .scale(y1)
      .ticks(ChartConst.GraphConfig.yAxis.ticks3)
      .tickFormat('')
      .tickSizeInner(-(width)) // To show horizontal gridlines
      .tickSizeOuter(0)
      .tickPadding(ChartConst.GraphConfig.yAxis.tickPadding);
    const tempArea = d3.area()
      .curve(ChartConst.bnd3Curve)
      .x((d) => x(d.x))
      .defined((d) => d.y !== null)
      .y0(chartHeight)
      .y1((d) => y1(d.y));
    // Temperature Line ChartConst
    const line1 = d3.line()
      .defined((d) => d.y !== null)
      .curve(ChartConst.bnd3Curve)
      .x((d) => x(d.x))
      .y((d) => y1(d.y));
    // RLH Line ChartConst
    const line2 = d3.line()
      .defined((d) => d.y !== null)
      .curve(ChartConst.bnd3Curve)
      .x((d) => x(d.x))
      .y((d) => y2(d.y));

    // Setting Domain for X-axis
    x.domain(d3.extent(data, (d) => d.forecast_ts));

    function getTempDomain() {
      // Adding offset to domain to add padding to line-area stroke
      //  so that it does not go outside chart bound
      const domain = d3.extent(data, (d) => d.tmp_display_value);
      return [domain[0] - ChartConst.tmpDomain.minOffset, domain[1] + ChartConst.tmpDomain
        .maxOffset,
      ];
    }
    const temperatureDomain = getTempDomain();
    // Setting Domain for Temperature
    y1.domain(temperatureDomain);
    // Setting Domain for RLH
    y2.domain([-1, 100]);
    // Drawing clear rect to seperate two charts
    chartObj.append('rect')
      .attr('class', 'clear-rect')
      .attr('x', 0)
      .attr('y', chartHeight)
      .attr('width', width)
      .attr('height', (topSpace / 2));
    // Drawing Y-axis left tick lines
    chartObj.append('g')
      .attr('class', 'y axis y-left y-grid-line-container')
      .call(yAxisLeftMinor);
    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-tick-highlighter')
      .call(yAxisLeftMinor);
    if (chartData && chartData.length) {
      // Temperature Gradient ChartConst
      chartObj.append('linearGradient')
        .attr('id', 'temperature-gradient')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0)
        .attr('y1', () => y1(temperatureConfig.minValue))
        .attr('x2', 0)
        .attr('y2', () => y1(temperatureConfig.maxValue))
        .selectAll('stop')
        .data(temperatureConfig.gradientConfig)
        .enter()
        .append('stop')
        .attr('offset', (d) => d.offset)
        .attr('stop-color', (d) => d.color);

      // Temperature Gradient AREA ChartConst
      chartObj.append('linearGradient')
        .attr('id', 'temperature-gradient-area')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0)
        .attr('y1', () => y1(temperatureConfig.minValue))
        .attr('x2', 0)
        .attr('y2', () => y1(temperatureConfig.maxValue))
        .selectAll('stop')
        .data(temperatureConfig.gradientConfigArea)
        .enter()
        .append('stop')
        .attr('offset', (d) => d.offset)
        .attr('stop-color', (d) => d.color);
    }
    const range = chartObj.selectAll('.series')
      .data(chartData)
      .enter().append('g')
      .attr('class', (d) => `series-${d.sequence}`);
    range.append('path')
      .attr('class', (d) => (d.area
        ? 'line line-area temp-line data-line-meteo'
        : 'line data-line-meteo'))
      .attr('d', (d) => (d.yAxis === 1
        ? line1(d.values)
        : line2(d.values)))
      .style('stroke', (d) => (d.area
        ? '#temperature-gradient'
        : d.color));
    range.append('path')
      .attr('class', (d) => (d.area
        ? 'temp-area'
        : ''))
      .attr('fill', (d) => d.color)
      .attr('d', (d) => {
        if (d.area) {
          // Draw area only if area flag is true for a given series
          return tempArea(d.values);
        }
        return null;
      });
    let temperatureLowHighObj;
    if (tempMinMaxPlottingData && tempMinMaxPlottingData.length) {
      temperatureLowHighObj = chartObj.append('g')
        .attr('id', 'temp-low-high')
        .selectAll('temperature-low-high')
        .data(tempMinMaxPlottingData)
        .enter()
        .append('g');
      temperatureLowHighObj.append('text')
        .text((d) => ((d && d.tmp_display_value)
          ? d.tmp_display_value.toFixed(0)
          : '--') + ChartConst.ChartLabelSpecs.tmp.tooltip.unit)
        .attr('class', 'shadow-text');
      temperatureLowHighObj.attr('transform', (d) => {
        const xValue = (x(d.forecast_ts) - bnd3Service.getSelectionWidth(temperatureLowHighObj
          .select('text')) / 2);
        const yValue = y1(d.tmp_display_value) - ChartConst.ChartLabelSpecs.tmp
          .highLowVerticalSpace;
        return `translate(${xValue}, ${yValue}) `;
      });
      temperatureLowHighObj.attr('opacity', (d) => {
        const xValue = (x(d.forecast_ts) - bnd3Service.getSelectionWidth(temperatureLowHighObj
          .select('text')) / 2);
        if (xValue < yAxisRectWidth || xValue > (width - yAxisRightRectWidth)) {
          return 0;
        }
        return 1;
      });
    }
    let baseTemperatureHorizontalRule;
    if (utilityService.isValueBetweenRange(
      temperatureConfig.baseValue,
      temperatureDomain[0],
      temperatureDomain[1],
    )) {
      // Drawing horizontal rule for base tempertature 32F/ 0C
      baseTemperatureHorizontalRule = chartObj.append('g')
        .attr('id', 'baseTemperatureHorizontalRule');
      baseTemperatureHorizontalRule.append('line')
        .classed('baseTempLine', true)
        .style(
          'stroke-dasharray', (
            `${ChartConst.temperature.baseValueRule.ruleStroke.onLength}, ${ChartConst.temperature.baseValueRule.ruleStroke.offLength}`
          ),
        )
        .attr('x1', yAxisRectWidth)
        .attr('y1', 0)
        .attr('x2', (width - yAxisRightRectWidth))
        .attr('y2', 0);
      baseTemperatureHorizontalRule.attr('transform', () => {
        const yValue = y1(temperatureConfig.baseValue);
        return `translate(0, ${yValue})`;
      });
    }
    // Drawing Y-axis left (Temperature)
    chartObj.append('g')
      .attr('class', 'y axis y-left y-left-negative-lines')
      .call(yAxisLeft);
    return chartData;
  };
  return content;
}
export default tempChartService();
